<script setup lang="ts">
import { motion, stagger, type MotionProps } from 'motion-v'
import { useVModel } from '@vueuse/core'
import type { NavigationMenu } from '~/types'
import { NuxtLink } from '#components'

const props = defineProps<{
  modelValue?: boolean
  menus: NavigationMenu[]
  activeNav?: string
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const isOpen = useVModel(props, 'modelValue', emits)

const selectedChildIndex = ref(-1)

const selectedChildNav = computed(
  () => props.menus[selectedChildIndex.value]?.child ?? undefined
)

const itemVariants: MotionProps['variants'] = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
      duration: 0.1
    }
  }
}

const resetSelectedChild = () => (selectedChildIndex.value = -1)

watch(isOpen, (value) => {
  if (!value) {
    selectedChildIndex.value = -1
  }
})

defineExpose({
  selectedChildNav,
  resetSelectedChild
})
</script>

<template>
  <div class="fixed top-navbar-m inset-x-0">
    <motion.div
      :animate="isOpen ? 'open' : 'closed'"
      class="bg-background dark:bg-surface-container overflow-x-hidden overflow-y-auto flex flex-col justify-between relative"
      :initial="false"
      :variants="{
        open: {
          height: 'calc(100dvh - var(--spacing-navbar-m))'
        },
        closed: {
          height: 0
        }
      }"
    >
      <motion.ul
        class="list-none flex flex-col divide-y divide-surface-container"
        :variants="{
          open: {
            transition: { delayChildren: stagger(0.1) }
          },
          closed: {
            transition: { delayChildren: stagger(0.05, { from: 'last' }) }
          }
        }"
      >
        <motion.li
          v-for="(nav, index) in menus"
          :key="index"
          class="list-none flex flex-col"
          :variants="itemVariants"
          :while-press="{ scale: 0.95 }"
        >
          <component
            :is="nav.to ? NuxtLink : 'button'"
            class="select-none px-6 py-3 text-sm font-bold no-underline outline-none flex justify-between items-center"
            :class="{ 'text-primary': activeNav === nav.title }"
            :to="nav.to"
            @click="nav.to ? undefined : (selectedChildIndex = index)"
          >
            <span>{{ nav.title }}</span>
            <Icon
              v-if="!nav.to"
              name="material-symbols:chevron-right"
              size="20px"
            />
          </component>
        </motion.li>
      </motion.ul>
      <motion.div
        class="flex flex-col gap-4 px-6 py-4 border-t-8 border-surface-container"
        :animate="
          isOpen ? (selectedChildIndex >= 0 ? 'closed' : 'open') : 'closed'
        "
        :variants="{
          open: { opacity: 1, transition: { delay: 0.2 } },
          closed: { opacity: 0, transition: { duration: 0.1 } }
        }"
      >
        <UiButton
          :to="$utm.utmUrl('/login')"
          label="Masuk"
          variant="primaryOutline"
        />
        <UiButton
          :to="$utm.utmUrlWithCustomMetaData('/kontak-kami')"
          label="Kontak Kami"
          variant="primaryOutline"
        />
        <UiButton :to="$utm.utmUrl()" label="Daftar Gratis" variant="primary" />
      </motion.div>
      <motion.ul
        class="list-none absolute inset-y-0 right-0 overflow-x-hidden overflow-y-auto bg-background dark:bg-surface-container flex flex-col"
        :animate="selectedChildIndex >= 0 ? 'open' : 'closed'"
        :initial="false"
        :variants="{
          open: {
            left: 0,
            transition: { delayChildren: stagger(0.1, { startDelay: 0.2 }) }
          },
          closed: {
            left: '100%',
            transition: { delayChildren: stagger(0.05, { from: 'last' }) }
          }
        }"
      >
        <motion.li
          v-for="(child, childIndex) in selectedChildNav ?? []"
          :key="childIndex"
          class="list-none"
          :class="{
            'bg-surface-container dark:bg-surface-container-high':
              child.quickLink
          }"
          :variants="itemVariants"
        >
          <p
            v-if="child.group"
            class="text-sm font-bold p-4 border-b border-surface-container"
            :class="[
              child.quickLink ? 'text-on-surface-variant' : 'text-outline'
            ]"
          >
            {{ child.group }}
          </p>
          <NavbarMenuItem
            v-for="(item, itemIndex) in child.items"
            :key="`${child.group}-${itemIndex}`"
            :title="item.title"
            :to="item.to"
            :icon="item.icon"
            :description="item.description"
            :quick-link="child.quickLink"
          />
        </motion.li>
      </motion.ul>
    </motion.div>
  </div>
</template>
